import { Space, SpaceVertical, type SpaceVerticalProps } from '@looker/components';
import { BINotification } from '@pulsepointinc/bi-components';
import { DsRadio, DsRadioGroup, DsTypography } from '@pulsepointinc/design-system-react';
import { type FC, useEffect, useId, useState } from 'react';
import type { ICustomFilter } from '../../../../../../helpers/helpers';
import { useFilters } from '../../../../../../hooks/useFilters';
import { TOUR_STEP_IDS } from '../../../../../../providers/TourProvider';
import type { Filter } from '../../../../../../stores/filters';
import useFilterSuggestions from '../../../hooks/useFilterSuggestions';

interface RadioButtonRecencyProps extends ICustomFilter, SpaceVerticalProps {
  onMultiFilterChange?: (filter: Filter) => void;
  filterName: string;
  label: string;
}

export const RadioButtonRecency: FC<RadioButtonRecencyProps> = ({
  onMultiFilterChange,
  onChange,
  filterName,
  label,
  ...spaceProps
}) => {
  const radioGroupId = useId();
  const { filters } = useFilters();
  const filterExpression = filters[filterName]?.expression ?? '';

  const { suggestions = [], defaultFilterValue } = useFilterSuggestions(filterName);

  const toggleOptions = suggestions;

  const [selectedOption, setSelectedOption] = useState(defaultFilterValue);
  const [showWarning, setShowWarning] = useState(false);

  const defaultFilter: Filter = {
    filterName,
    expression: selectedOption,
    enabled: true,
    expressionType: 'target',
  };

  useEffect(() => {
    if (onMultiFilterChange) {
      const filterToSend = filterExpression ? { ...defaultFilter, expression: filterExpression } : defaultFilter;

      onMultiFilterChange(filterToSend);
    }
  }, [filterExpression]);

  useEffect(() => {
    if (filterExpression) {
      setSelectedOption(filterExpression);
    }
  }, [filterExpression]);

  const handleRadioChange = (value: string) => {
    setSelectedOption(value);

    if (filterExpression) {
      setShowWarning(true);
    }

    const filter: Filter = {
      filterName,
      expression: value,
      enabled: true,
      expressionType: defaultFilter.expressionType,
    };

    if (onMultiFilterChange) {
      onMultiFilterChange(filter);
    }
    if (onChange) {
      onChange(filter.expression);
    }
  };

  const warningMessage = `${label} recency selection will be carried across all ${label} filters`;

  return (
    <SpaceVertical gap="u3" paddingX="u6" data-tour-id={TOUR_STEP_IDS.CLINICAL_RECENCY_FILTER} {...spaceProps}>
      <Space gap="u4" align="center">
        <DsTypography variant="label">Recency</DsTypography>

        <DsRadioGroup
          name={radioGroupId}
          orientation="horizontal"
          value={selectedOption}
          onValueChange={(e) => handleRadioChange(e.detail as string)}
        >
          {toggleOptions.map((option: string) => (
            <DsRadio key={option} name={radioGroupId} label={option} value={option} />
          ))}
        </DsRadioGroup>
      </Space>

      {showWarning && <BINotification text={warningMessage} intent="warning" />}
    </SpaceVertical>
  );
};

export default RadioButtonRecency;
